import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { fechaParaMirar, hoyLocal } from '../lib/fecha';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { Boton, Insignia, Tarjeta } from './ui/basicos';
import { Dialogo } from './ui/dialogo';
import { Campo, Selector } from './ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from './ui/estados';
import { Tabla, type Columna } from './ui/tabla';

export interface ListaDePrecios {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly monedaCodigo: string;
  readonly expresion: 'neto' | 'final';
  readonly esPredeterminada: boolean;
  readonly activa: boolean;
  readonly vigenteDesde: string | null;
  readonly vigenteHasta: string | null;
  readonly tieneVentas: boolean;
  readonly escalas: readonly { desdeCantidad: string; porcentaje: string }[];
}

interface Alta {
  codigo: string;
  nombre: string;
  monedaCodigo: string;
  expresion: 'neto' | 'final';
  vigenteDesde: string;
  vigenteHasta: string;
  copiarDe: string;
  porcentaje: string;
  multiplo: string;
}

const ALTA_VACIA: Alta = {
  codigo: '',
  nombre: '',
  monedaCodigo: '',
  expresion: 'final',
  vigenteDesde: '',
  vigenteHasta: '',
  copiarDe: '',
  porcentaje: '0',
  multiplo: '',
};

/** «1» y no «1.000», «5» y no «5.0000»: los números de la base traen ceros que no dicen nada. */
function sinCeros(numero: string): string {
  return numero.includes('.') ? numero.replace(/\.?0+$/, '') : numero;
}

/**
 * Las listas de precios del comercio, en Configuración (E36).
 *
 * Una sola pantalla para lo que una lista es: su nombre, desde y hasta cuándo vale, sus tramos por
 * cantidad, si es la predeterminada y si sigue en uso. **Eliminar borra o desactiva según la lista
 * haya vendido o no**, y la pantalla dice cuál de las dos hizo: quien elimina una lista vieja tiene
 * que saber que sus ventas siguen diciendo con qué lista se hicieron.
 *
 * Lo que no se ofrece es cambiarle la moneda o la expresión a una lista que ya existe: reinterpreta
 * cada precio cargado (E15).
 */
export function ListasDePrecios(): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeVer = usePermiso('PRECIOS.LISTA.VER');
  const puedeCrear = usePermiso('PRECIOS.LISTA.CREAR');
  const puedeEditar = usePermiso('PRECIOS.LISTA.EDITAR');

  const [alta, setAlta] = useState<Alta | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<ListaDePrecios | null>(null);
  const [edicion, setEdicion] = useState({ codigo: '', nombre: '', desde: '', hasta: '' });
  const [conTramos, setConTramos] = useState<ListaDePrecios | null>(null);
  const [tramos, setTramos] = useState<{ desdeCantidad: string; porcentaje: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    enabled: puedeVer,
    queryFn: () => pedir<ListaDePrecios[]>('/listas-de-precios'),
  });
  const monedas = useQuery({
    queryKey: ['monedas'],
    enabled: puedeVer,
    queryFn: () => pedir<{ monedaCodigo: string; esBase: boolean }[]>('/monedas'),
  });

  const refrescar = async (): Promise<void> => {
    await cola.invalidateQueries({ queryKey: ['listas-de-precios'] });
  };
  const alFallar = (fallo: unknown): void => {
    setError(textoDeError(fallo));
  };

  const crear = useMutation({
    mutationFn: (datos: Alta) =>
      pedir<{ id: string; copiados: number }>('/listas-de-precios', {
        method: 'POST',
        cuerpo: {
          codigo: datos.codigo.trim(),
          nombre: datos.nombre.trim(),
          monedaCodigo: datos.monedaCodigo,
          expresion: datos.expresion,
          vigenteDesde: datos.vigenteDesde === '' ? null : datos.vigenteDesde,
          vigenteHasta: datos.vigenteHasta === '' ? null : datos.vigenteHasta,
          ...(datos.copiarDe === ''
            ? {}
            : {
                copiarDe: {
                  listaId: datos.copiarDe,
                  porcentaje: datos.porcentaje.trim() === '' ? '0' : datos.porcentaje.trim(),
                  redondeo:
                    datos.multiplo.trim() === ''
                      ? { clase: 'decimales', decimales: 2 }
                      : { clase: 'multiplo', multiplo: datos.multiplo.trim() },
                },
              }),
        },
      }),
    onSuccess: async (respuesta) => {
      setAlta(null);
      setError(null);
      setAviso(
        respuesta.copiados > 0
          ? t('listasDePrecios.copiados', { cantidad: respuesta.copiados })
          : null,
      );
      await refrescar();
    },
    onError: alFallar,
  });

  const corregir = useMutation({
    mutationFn: () => {
      const original = corrigiendo;
      const cambios: Record<string, unknown> = {};
      if (original !== null) {
        if (edicion.codigo.trim() !== original.codigo) cambios.codigo = edicion.codigo.trim();
        if (edicion.nombre.trim() !== original.nombre) cambios.nombre = edicion.nombre.trim();
        // Vaciar una fecha es «sin límite de ese lado», que se manda como nulo.
        if (edicion.desde !== (original.vigenteDesde ?? '')) {
          cambios.vigenteDesde = edicion.desde === '' ? null : edicion.desde;
        }
        if (edicion.hasta !== (original.vigenteHasta ?? '')) {
          cambios.vigenteHasta = edicion.hasta === '' ? null : edicion.hasta;
        }
      }
      return pedir<void>(`/listas-de-precios/${original?.id ?? ''}`, {
        method: 'PATCH',
        cuerpo: cambios,
      });
    },
    onSuccess: async () => {
      setCorrigiendo(null);
      setError(null);
      await refrescar();
    },
    onError: alFallar,
  });

  const guardarTramos = useMutation({
    mutationFn: () =>
      pedir<void>(`/listas-de-precios/${conTramos?.id ?? ''}/escalas`, {
        method: 'PUT',
        cuerpo: {
          escalas: tramos
            .filter((tramo) => tramo.desdeCantidad.trim() !== '' && tramo.porcentaje.trim() !== '')
            .map((tramo) => ({
              desdeCantidad: tramo.desdeCantidad.trim(),
              porcentaje: tramo.porcentaje.trim(),
            })),
        },
      }),
    onSuccess: async () => {
      setConTramos(null);
      setError(null);
      await refrescar();
    },
    onError: alFallar,
  });

  const predeterminada = useMutation({
    mutationFn: (fila: ListaDePrecios) =>
      pedir<void>(`/listas-de-precios/${fila.id}/predeterminada`, { method: 'POST' }),
    onSuccess: refrescar,
    onError: (fallo: unknown) => {
      setAviso(textoDeError(fallo));
    },
  });

  const eliminar = useMutation({
    mutationFn: (fila: ListaDePrecios) =>
      pedir<{ resultado: 'eliminada' | 'desactivada' }>(`/listas-de-precios/${fila.id}`, {
        method: 'DELETE',
      }),
    onSuccess: async (respuesta) => {
      setAviso(
        respuesta.resultado === 'eliminada'
          ? t('listasDePrecios.eliminada')
          : t('listasDePrecios.desactivada'),
      );
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setAviso(textoDeError(fallo));
    },
  });

  const activar = useMutation({
    mutationFn: (fila: ListaDePrecios) =>
      pedir<void>(`/listas-de-precios/${fila.id}/activacion`, { method: 'POST' }),
    onSuccess: refrescar,
    onError: (fallo: unknown) => {
      setAviso(textoDeError(fallo));
    },
  });

  if (!puedeVer) return null;

  const hoy = hoyLocal();
  const legible = (fecha: string): string => fechaParaMirar(fecha, i18n.language);
  const vigencia = (fila: ListaDePrecios): string => {
    if (fila.vigenteDesde === null && fila.vigenteHasta === null)
      return t('listasDePrecios.siempre');
    if (fila.vigenteDesde === null)
      return t('listasDePrecios.hasta', { hasta: legible(fila.vigenteHasta ?? '') });
    if (fila.vigenteHasta === null)
      return t('listasDePrecios.desde', { desde: legible(fila.vigenteDesde) });
    return t('listasDePrecios.entre', {
      desde: legible(fila.vigenteDesde),
      hasta: legible(fila.vigenteHasta),
    });
  };
  const fueraDeFecha = (fila: ListaDePrecios): boolean =>
    (fila.vigenteDesde !== null && hoy < fila.vigenteDesde) ||
    (fila.vigenteHasta !== null && hoy > fila.vigenteHasta);

  const columnas: Columna<ListaDePrecios>[] = [
    {
      clave: 'nombre',
      titulo: t('listasDePrecios.nombre'),
      contenido: (fila) => (
        <>
          <span className="block font-medium">{fila.nombre}</span>
          <span className="block text-xs text-texto-tenue">{fila.codigo}</span>
        </>
      ),
    },
    {
      clave: 'moneda',
      titulo: t('listasDePrecios.moneda'),
      contenido: (fila) => (
        <>
          <span className="block">{fila.monedaCodigo}</span>
          <span className="block text-xs text-texto-tenue">
            {t(`listasDePrecios.expresion.${fila.expresion}`)}
          </span>
        </>
      ),
    },
    { clave: 'vigencia', titulo: t('listasDePrecios.vigencia'), contenido: vigencia },
    {
      clave: 'tramos',
      titulo: t('listasDePrecios.tramos'),
      contenido: (fila) =>
        fila.escalas.length === 0
          ? t('listasDePrecios.sinTramos')
          : fila.escalas
              .map((escala) =>
                t('listasDePrecios.tramo', {
                  desde: sinCeros(escala.desdeCantidad),
                  porcentaje: sinCeros(escala.porcentaje),
                }),
              )
              .join(' · '),
    },
    {
      clave: 'estado',
      titulo: t('listasDePrecios.estado'),
      contenido: (fila) =>
        fila.esPredeterminada ? (
          <Insignia tono="bien">{t('listasDePrecios.predeterminada')}</Insignia>
        ) : !fila.activa ? (
          <Insignia tono="neutro">{t('listasDePrecios.inactiva')}</Insignia>
        ) : fueraDeFecha(fila) ? (
          <Insignia tono="atencion">{t('listasDePrecios.fueraDeFecha')}</Insignia>
        ) : (
          <Insignia tono="bien">{t('listasDePrecios.activa')}</Insignia>
        ),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) =>
        !puedeEditar ? null : (
          <div className="flex flex-wrap justify-end gap-1">
            <Boton
              onClick={() => {
                setCorrigiendo(fila);
                setEdicion({
                  codigo: fila.codigo,
                  nombre: fila.nombre,
                  desde: fila.vigenteDesde ?? '',
                  hasta: fila.vigenteHasta ?? '',
                });
                setError(null);
              }}
            >
              {t('acciones.corregir')}
            </Boton>
            <Boton
              onClick={() => {
                setConTramos(fila);
                setTramos(
                  fila.escalas.length === 0
                    ? [{ desdeCantidad: '', porcentaje: '' }]
                    : fila.escalas.map((escala) => ({
                        desdeCantidad: sinCeros(escala.desdeCantidad),
                        porcentaje: sinCeros(escala.porcentaje),
                      })),
                );
                setError(null);
              }}
            >
              {t('listasDePrecios.tramos')}
            </Boton>
            {fila.esPredeterminada ? null : (
              <Boton
                onClick={() => {
                  predeterminada.mutate(fila);
                }}
              >
                {t('listasDePrecios.hacerPredeterminada')}
              </Boton>
            )}
            {fila.esPredeterminada ? null : fila.activa ? (
              <Boton
                onClick={() => {
                  eliminar.mutate(fila);
                }}
              >
                {fila.tieneVentas ? t('listasDePrecios.desactivar') : t('listasDePrecios.eliminar')}
              </Boton>
            ) : (
              <Boton
                onClick={() => {
                  activar.mutate(fila);
                }}
              >
                {t('listasDePrecios.activar')}
              </Boton>
            )}
          </div>
        ),
    },
  ];

  const base = monedas.data?.find((moneda) => moneda.esBase)?.monedaCodigo ?? '';

  return (
    <Tarjeta>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{t('listasDePrecios.titulo')}</h2>
          <p className="text-xs text-texto-tenue">{t('listasDePrecios.descripcion')}</p>
        </div>
        {puedeCrear ? (
          <Boton
            tono="principal"
            onClick={() => {
              setAlta({ ...ALTA_VACIA, monedaCodigo: base });
              setError(null);
            }}
          >
            {t('listasDePrecios.nueva')}
          </Boton>
        ) : null}
      </div>

      {aviso === null ? null : (
        <p role="status" className="mb-2 text-apoyo">
          {aviso}
        </p>
      )}

      {listas.isPending ? <Cargando /> : null}
      {listas.isError ? (
        <Problema
          error={listas.error}
          reintentar={() => {
            void listas.refetch();
          }}
        />
      ) : null}
      {listas.isSuccess ? (
        <Tabla
          columnas={columnas}
          filas={listas.data}
          claveDeFila={(fila) => fila.id}
          vacio={<Vacio titulo={t('listasDePrecios.vacio')} />}
        />
      ) : null}

      {/* Alta, con la opción de nacer copiando otra lista. */}
      <Dialogo
        abierto={alta !== null}
        titulo={t('listasDePrecios.nueva')}
        onCerrar={() => {
          setAlta(null);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            disabled={alta === null || alta.codigo.trim() === '' || alta.nombre.trim() === ''}
            onClick={() => {
              if (alta !== null) crear.mutate(alta);
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        {alta === null ? null : (
          <div className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta={t('listasDePrecios.codigo')}
                obligatorio
                value={alta.codigo}
                maxLength={32}
                onChange={(evento) => {
                  setAlta({ ...alta, codigo: evento.target.value });
                }}
              />
              <Campo
                etiqueta={t('listasDePrecios.nombre')}
                obligatorio
                value={alta.nombre}
                maxLength={120}
                onChange={(evento) => {
                  setAlta({ ...alta, nombre: evento.target.value });
                }}
              />
            </div>
            <Selector
              etiqueta={t('listasDePrecios.copiarDe')}
              value={alta.copiarDe}
              ayuda={t('listasDePrecios.ayudaCopiar')}
              onChange={(evento) => {
                setAlta({ ...alta, copiarDe: evento.target.value });
              }}
            >
              <option value="">{t('listasDePrecios.sinCopiar')}</option>
              {(listas.data ?? []).map((lista) => (
                <option key={lista.id} value={lista.id}>
                  {lista.nombre}
                </option>
              ))}
            </Selector>
            {alta.copiarDe === '' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Selector
                  etiqueta={t('listasDePrecios.moneda')}
                  value={alta.monedaCodigo}
                  onChange={(evento) => {
                    setAlta({ ...alta, monedaCodigo: evento.target.value });
                  }}
                >
                  {(monedas.data ?? []).map((moneda) => (
                    <option key={moneda.monedaCodigo} value={moneda.monedaCodigo}>
                      {moneda.monedaCodigo}
                    </option>
                  ))}
                </Selector>
                <Selector
                  etiqueta={t('listasDePrecios.expresionDelPrecio')}
                  value={alta.expresion}
                  ayuda={t('listasDePrecios.ayudaExpresion')}
                  onChange={(evento) => {
                    setAlta({
                      ...alta,
                      expresion: evento.target.value === 'neto' ? 'neto' : 'final',
                    });
                  }}
                >
                  <option value="final">{t('listasDePrecios.expresion.final')}</option>
                  <option value="neto">{t('listasDePrecios.expresion.neto')}</option>
                </Selector>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Campo
                  etiqueta={t('listasDePrecios.porcentaje')}
                  value={alta.porcentaje}
                  ayuda={t('listasDePrecios.ayudaPorcentaje')}
                  inputMode="decimal"
                  onChange={(evento) => {
                    setAlta({ ...alta, porcentaje: evento.target.value });
                  }}
                />
                <Campo
                  etiqueta={t('listasDePrecios.redondearA')}
                  value={alta.multiplo}
                  ayuda={t('listasDePrecios.ayudaRedondeo')}
                  inputMode="decimal"
                  onChange={(evento) => {
                    setAlta({ ...alta, multiplo: evento.target.value });
                  }}
                />
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta={t('listasDePrecios.vigenteDesde')}
                type="date"
                value={alta.vigenteDesde}
                onChange={(evento) => {
                  setAlta({ ...alta, vigenteDesde: evento.target.value });
                }}
              />
              <Campo
                etiqueta={t('listasDePrecios.vigenteHasta')}
                type="date"
                value={alta.vigenteHasta}
                onChange={(evento) => {
                  setAlta({ ...alta, vigenteHasta: evento.target.value });
                }}
              />
            </div>
            <p className="text-xs text-texto-tenue">{t('listasDePrecios.ayudaVigencia')}</p>
            {error === null ? null : (
              <p role="alert" className="text-apoyo text-mal">
                {error}
              </p>
            )}
          </div>
        )}
      </Dialogo>

      {/* Corregir: nombre, código y vigencia. La moneda y la expresión no se corrigen. */}
      <Dialogo
        abierto={corrigiendo !== null}
        titulo={t('listasDePrecios.corregir')}
        onCerrar={() => {
          setCorrigiendo(null);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={corregir.isPending}
            disabled={edicion.codigo.trim() === '' || edicion.nombre.trim() === ''}
            onClick={() => {
              corregir.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etiqueta={t('listasDePrecios.codigo')}
              obligatorio
              value={edicion.codigo}
              maxLength={32}
              onChange={(evento) => {
                setEdicion((previo) => ({ ...previo, codigo: evento.target.value }));
              }}
            />
            <Campo
              etiqueta={t('listasDePrecios.nombre')}
              obligatorio
              value={edicion.nombre}
              maxLength={120}
              onChange={(evento) => {
                setEdicion((previo) => ({ ...previo, nombre: evento.target.value }));
              }}
            />
          </div>
          {corrigiendo?.esPredeterminada === true ? (
            <p className="text-xs text-texto-tenue">
              {t('listasDePrecios.predeterminadaSinVigencia')}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta={t('listasDePrecios.vigenteDesde')}
                type="date"
                value={edicion.desde}
                onChange={(evento) => {
                  setEdicion((previo) => ({ ...previo, desde: evento.target.value }));
                }}
              />
              <Campo
                etiqueta={t('listasDePrecios.vigenteHasta')}
                type="date"
                value={edicion.hasta}
                onChange={(evento) => {
                  setEdicion((previo) => ({ ...previo, hasta: evento.target.value }));
                }}
              />
            </div>
          )}
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      {/* Tramos por cantidad: se mandan todos juntos y reemplazan a los que había. */}
      <Dialogo
        abierto={conTramos !== null}
        titulo={t('listasDePrecios.tramosDe', { lista: conTramos?.nombre ?? '' })}
        onCerrar={() => {
          setConTramos(null);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={guardarTramos.isPending}
            onClick={() => {
              guardarTramos.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-xs text-texto-tenue">{t('listasDePrecios.ayudaTramos')}</p>
          {tramos.map((tramo, indice) => (
            <div key={indice} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
              <Campo
                etiqueta={t('listasDePrecios.desdeCantidad')}
                value={tramo.desdeCantidad}
                inputMode="decimal"
                onChange={(evento) => {
                  setTramos(
                    tramos.map((otro, cual) =>
                      cual === indice ? { ...otro, desdeCantidad: evento.target.value } : otro,
                    ),
                  );
                }}
              />
              <Campo
                etiqueta={t('listasDePrecios.descuento')}
                value={tramo.porcentaje}
                inputMode="decimal"
                onChange={(evento) => {
                  setTramos(
                    tramos.map((otro, cual) =>
                      cual === indice ? { ...otro, porcentaje: evento.target.value } : otro,
                    ),
                  );
                }}
              />
              <Boton
                tono="sutil"
                onClick={() => {
                  setTramos(tramos.filter((_, cual) => cual !== indice));
                }}
              >
                {t('acciones.quitar')}
              </Boton>
            </div>
          ))}
          <div>
            <Boton
              onClick={() => {
                setTramos([...tramos, { desdeCantidad: '', porcentaje: '' }]);
              }}
            >
              {t('listasDePrecios.agregarTramo')}
            </Boton>
          </div>
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
    </Tarjeta>
  );
}
